import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import './Modal.css';
import { TransactionType } from '../../types/drivers';

interface CreateDriverTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (transactionType: TransactionType, amount: number, description: string) => Promise<void>;
  driverName: string;
}

const CreateDriverTransactionModal: React.FC<CreateDriverTransactionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  driverName
}) => {
  const { t } = useTranslation();
  const [transactionType, setTransactionType] = useState<TransactionType>(TransactionType.FILL);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuickTextClick = () => {
    setDescription(t('modals.createDriverTransaction.quickText'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amountNum = parseInt(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert(t('modals.createDriverTransaction.validationAmount'));
      return;
    }

    if (!description.trim()) {
      alert(t('modals.createDriverTransaction.validationDescription'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(transactionType, amountNum, `<p>${description}</p>`);
      setAmount('');
      setDescription('');
      setTransactionType(TransactionType.FILL);
      onClose();
    } catch (error) {
      console.error('Failed to create transaction:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setAmount('');
      setDescription('');
      setTransactionType(TransactionType.FILL);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('modals.createDriverTransaction.title')}
      maxWidth="600px"
    >
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label>{t('modals.createDriverTransaction.transactionType')}</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <label
              style={{
                flex: 1,
                padding: '10px',
                border: `2px solid ${transactionType === TransactionType.FILL ? '#10b981' : '#ddd'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                backgroundColor: transactionType === TransactionType.FILL ? '#ecfdf5' : 'white',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="radio"
                value={TransactionType.FILL}
                checked={transactionType === TransactionType.FILL}
                onChange={(e) => setTransactionType(e.target.value as TransactionType)}
                style={{ marginRight: '8px' }}
              />
              {t('modals.createDriverTransaction.fill')}
            </label>
            <label
              style={{
                flex: 1,
                padding: '10px',
                border: `2px solid ${transactionType === TransactionType.WITHDRAW ? '#ef4444' : '#ddd'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                backgroundColor: transactionType === TransactionType.WITHDRAW ? '#fef2f2' : 'white',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="radio"
                value={TransactionType.WITHDRAW}
                checked={transactionType === TransactionType.WITHDRAW}
                onChange={(e) => setTransactionType(e.target.value as TransactionType)}
                style={{ marginRight: '8px' }}
              />
              {t('modals.createDriverTransaction.withdraw')}
            </label>
          </div>
        </div>

        <div className="modal-form-group">
          <label htmlFor="amount">{t('modals.createDriverTransaction.amount')}</label>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t('modals.createDriverTransaction.amountPlaceholder')}
            required
            min="1"
            disabled={isSubmitting}
          />
          <small style={{ color: '#6b7280', fontSize: '13px' }}>
            {t('modals.createDriverTransaction.amountHint')}
          </small>
        </div>

        <div className="modal-form-group">
          <label htmlFor="description">{t('modals.createDriverTransaction.description')}</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('modals.createDriverTransaction.descriptionPlaceholder')}
            rows={4}
            required
            disabled={isSubmitting}
            style={{ resize: 'vertical' }}
          />
          <button
            type="button"
            onClick={handleQuickTextClick}
            disabled={isSubmitting}
            style={{
              marginTop: '8px',
              padding: '6px 12px',
              fontSize: '13px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('modals.createDriverTransaction.quickText')}
          </button>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="action-button accept"
            style={{ backgroundColor: '#6b7280' }}
          >
            {t('modals.createDriverTransaction.cancel')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="action-button accept"
            style={{ backgroundColor: '#3b82f6' }}
          >
            {isSubmitting
              ? t('modals.createDriverTransaction.creating')
              : t('modals.createDriverTransaction.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateDriverTransactionModal;
